import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { PublicLayout } from "@/components/Navbar";
import { ArrowLeft, Lock, ListPlus, ArrowRight, ZoomIn, X } from "lucide-react";

export default function ProductDetail() {
  const { sku } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    api.get(`/public/products/${sku}`).then(({ data }) => setProduct(data)).catch(() => setNotFound(true));
  }, [sku]);

  const addToEnquiry = () => {
    const list = JSON.parse(localStorage.getItem("mc_enquiry") || "[]");
    if (list.find((i) => i.sku === product.sku)) { toast.info("Already on your enquiry list"); return; }
    list.push({ sku: product.sku, name: product.name, brand: product.brand, image_url: product.image_url });
    localStorage.setItem("mc_enquiry", JSON.stringify(list));
    toast.success(`${product.name} added to enquiry list`);
  };

  if (notFound) return <PublicLayout><div className="mx-auto max-w-3xl px-4 py-32 text-center text-slate-300">Product not found. <Link to="/catalog" className="text-[#D4AF37]">Back to catalogue</Link></div></PublicLayout>;
  if (!product) return <PublicLayout><div className="py-40 text-center text-[#64748B]">Loading…</div></PublicLayout>;

  const HIDDEN_SPECS = ["Allergens", "Case Weight", "Prep"];
  const specRows = [
    ["SKU / Product Code", product.sku],
    ["Brand", product.brand],
    ["Pack Size", product.pack_size],
    ["Country of Origin", product.country_of_origin],
    ["Storage Type", product.storage_type],
    ...Object.entries(product.specifications || {}).filter(([k]) => !HIDDEN_SPECS.includes(k)),
  ];

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
        <Link to="/catalog" className="inline-flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-[#D4AF37]"><ArrowLeft className="h-4 w-4" /> Back to catalogue</Link>
        <div className="mt-6 grid gap-10 lg:grid-cols-2">
          <button type="button" onClick={() => setZoom(true)} data-testid="product-image-zoom"
            className="group relative mx-auto block w-full max-w-[500px] cursor-zoom-in overflow-hidden rounded-3xl border border-[#2A2E39] bg-white">
            <img src={product.image_url} alt={product.name} className="aspect-square w-full object-contain p-6" />
            <span className="absolute left-4 top-4 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#D4AF37] backdrop-blur">{product.brand}</span>
            <span className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold text-white opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
              <ZoomIn className="h-3.5 w-3.5" /> Click to zoom
            </span>
          </button>

          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">{product.category}</span>
            <h1 className="mt-2 font-serif-display text-4xl font-bold text-slate-50">{product.name}</h1>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#94A3B8]">
              <span>SKU / Product Code: <span className="font-mono-data text-slate-200">{product.sku}</span></span>
              <span>Pack Size: <span className="text-slate-200">{product.pack_size}</span></span>
            </div>

            {/* locked pricing */}
            <div data-testid="public-locked-pricing" className="mt-6 flex items-start gap-3 rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-5">
              <Lock className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" />
              <div>
                <div className="font-semibold text-slate-100">Trade pricing & live stock are protected</div>
                <p className="mt-1 text-sm text-[#94A3B8]">Wholesale pricing and exact availability are visible only to approved M&C trade accounts.</p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/apply" data-testid="apply-trade-account-btn" className="mc-btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5">
                Apply for Trade Account <ArrowRight className="h-4 w-4" />
              </Link>
              <button onClick={addToEnquiry} data-testid="add-to-enquiry-list-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#2A2E39] px-7 py-3.5 font-semibold text-slate-100 transition-colors hover:border-[#D4AF37]/50 hover:text-[#D4AF37]">
                <ListPlus className="h-4 w-4" /> Add to Enquiry List
              </button>
            </div>

            {/* specs */}
            <div className="mt-8 overflow-hidden rounded-2xl border border-[#2A2E39]">
              <div className="border-b border-[#2A2E39] bg-[#1A1D24] px-5 py-3 text-sm font-semibold text-slate-100">Product Specifications</div>
              <dl className="divide-y divide-[#2A2E39]">
                {specRows.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 px-5 py-3 text-sm">
                    <dt className="text-[#94A3B8]">{k}</dt>
                    <dd className="text-right font-medium text-slate-100 font-mono-data">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </div>

      {zoom && (
        <div data-testid="zoom-overlay" onClick={() => setZoom(false)}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm">
          <button data-testid="zoom-close" onClick={() => setZoom(false)}
            className="absolute right-5 top-5 rounded-full border border-white/20 p-2.5 text-white hover:bg-white/10"><X className="h-5 w-5" /></button>
          <img src={product.image_url} alt={product.name} onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[90vw] rounded-2xl bg-white object-contain p-4" />
        </div>
      )}
    </PublicLayout>
  );
}
